import React, {useState, useRef, useEffect, useLayoutEffect} from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';

// icons
import CommentIcon from '@mui/icons-material/Comment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// components
import {AllergySection} from '../components/AllergySection';
import {GiftVoucherSection} from '../components/GiftVoucherSection';
import {CustomQuestionsSection} from '../components/CustomQuestionsSection';

// constants
import {OCCASION_VALUES, getOccasionLabel as getOccasionLabelUtil} from 'packages/common/constants/restaurants/occasionTypes';
import {ERROR_MESSAGES} from '../constants/reservation';
import {InlineNotice} from 'packages/common/components/inline-notice';
import {clientInputStyles} from '../styles/ReservationFlowStyles';

// utils
import {isAllergyDeclarationComplete, ALLERGY_DETAILS_ERROR_KEY} from 'packages/common/utils/restaurant/allergyDetailsRule';
import {findUnansweredRequiredQuestions} from 'packages/common/utils/restaurant/customAnswers';

/**
 * @file Étape "détails supplémentaires" du parcours de réservation de table.
 * @module packages.common.components.table-reservation.sections.AdditionalDetailsStep
 */

// ----------------------------------------------------------------------

// Conteneur pour la page de détails avec hauteur adaptative
const DetailsContainer = styled(Box)(({theme}) => ({
  maxWidth: '100%',
  padding: theme.spacing(0, 0, 0),
}));

// Custom select avec OutlinedInput + Popover
const CustomSelect = styled(OutlinedInput)({
  cursor: 'pointer',
  ...clientInputStyles,
});

// Style pour le menu déroulant personnalisé avec Popper
const CustomMenuPaper = styled(Paper)(({theme}) => ({
  marginTop: theme.spacing(1),
  boxShadow: theme.shadows[8],
  maxHeight: 250,
  overflow: 'auto',
}));

const CustomListItem = styled(ListItem)(({theme, isSelected}) => ({
  'cursor': 'pointer',
  'transition': theme.transitions.create(['background-color']),
  'padding': theme.spacing(1, 2),
  ...(isSelected && {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

// Champ de texte pour les demandes spéciales
const SpecialRequestsInput = styled(OutlinedInput)({
  cursor: 'text',
  width: '100%',
  ...clientInputStyles,
});

// ----------------------------------------------------------------------

/**
 * Étape du parcours de réservation regroupant allergies, bon cadeau, occasion,
 * demandes spéciales et questions personnalisées de l'établissement. Chaque
 * champ standard n'est rendu que si activé dans la configuration du restaurant
 * (`config.optionalFields`, activé par défaut si absent) ; les questions
 * personnalisées viennent de `config.customQuestions`.
 *
 * Répondre reste facultatif, sauf deux cas qui bloquent l'étape : signaler des
 * allergies sans les détailler, une alerte sans contenu n'étant pas exploitable
 * en cuisine, et laisser sans réponse une question que l'établissement a
 * rendue obligatoire.
 *
 * @param {Object} props - Props du composant.
 * @param {Object} props.formData - Données courantes du formulaire de réservation.
 * @param {Function} props.updateFormData - Met à jour un champ (field, value).
 * @param {Object} [props.config] - Configuration du restaurant, dont `optionalFields` et `customQuestions`.
 * @param {Function} props.setValidation - Enregistre la fonction de validation de l'étape auprès du parent.
 * @param {Function} props.setErrors - Met à jour les erreurs du formulaire.
 * @param {Object} [props.errors] - Erreurs courantes (dont `errors.api`).
 * @param {Function} [props.setStepReady] - Signale au parent que l'étape est prête.
 * @param {Function} [props.t] - Fonction de traduction i18next.
 * @return {JSX.Element} Étape de détails supplémentaires.
 */
export function AdditionalDetailsStep({
  formData,
  updateFormData,
  config,
  setValidation,
  setErrors,
  errors,
  setStepReady,
  t, // Translation function
}) {
  // Utilise la fonction utilitaire partagée pour obtenir les labels traduits
  const getOccasionLabel = React.useCallback((key) => getOccasionLabelUtil(key, t), [t]);

  // État et refs pour le menu déroulant personnalisé
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const inputRef = useRef(null);
  const allergySectionRef = useRef(null);
  const occasionSelectRef = useRef(null);

  // Activation par champ (cf. settings.optionalFields côté restaurant). Un champ
  // absent du payload est considéré activé pour rester compatible avec les
  // anciennes configurations restaurant.
  const showAllergies = config?.optionalFields?.allergies !== false;
  const showGiftVoucher = config?.optionalFields?.giftVoucher !== false;
  const showOccasion = config?.optionalFields?.occasion !== false;
  const showSpecialRequests = config?.optionalFields?.specialRequests !== false;

  // Le reproche visuel (rouge) attend que le client ait quitté le champ ou tenté
  // d'avancer : le champ de détails apparaît au moment même où le bouton se grise,
  // le peindre en rouge aussitôt serait punitif. L'explication, elle, est immédiate.
  const [allergyTouched, setAllergyTouched] = useState(false);

  // Question coupée par le restaurateur : la règle ne doit jamais bloquer.
  const allergyComplete = !showAllergies || isAllergyDeclarationComplete(formData);
  const allergyMessage = t ? t(ALLERGY_DETAILS_ERROR_KEY) : ERROR_MESSAGES.ALLERGY_DETAILS_REQUIRED;

  // Questions personnalisées : rien n'est reproché à l'arrivée, l'astérisque
  // suffit. Le bouton reste actif et c'est la tentative d'avancer qui signale
  // la question manquante (rouge, bandeau, défilement jusqu'à elle).
  const customQuestions = config?.customQuestions || [];
  const customQuestionsRef = useRef(null);
  const [customAnswersTouched, setCustomAnswersTouched] = useState(false);
  // Valeur simple, et non le tableau recalculé à chaque rendu : en dépendance de
  // `validateStep`, un tableau neuf relancerait l'enregistrement de la validation
  // auprès du parent, donc un rendu, à l'infini.
  const firstUnansweredId = findUnansweredRequiredQuestions(customQuestions, formData.customAnswers)[0]?.id;
  const customAnswersComplete = !firstUnansweredId;
  const stepComplete = allergyComplete && customAnswersComplete;

  // Auto-focus le premier champ visible pour améliorer l'accessibilité.
  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      if (showAllergies && allergySectionRef.current) {
        allergySectionRef.current.focus?.();
      } else if (showOccasion && occasionSelectRef.current) {
        occasionSelectRef.current.focus?.();
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [showAllergies, showOccasion]);

  // Validation synchrone, et enregistrée sans double flèche : `setValidationFunction`
  // enveloppe déjà la valeur reçue, une seconde enveloppe ferait renvoyer la fonction
  // au lieu de l'appeler, et le verrou serait inerte.
  const validateStep = React.useCallback(() => {
    if (stepComplete) return true;

    // Le message s'affiche sous le champ et dans le bandeau ; rien n'est écrit
    // dans `errors`, que `setErrors` remplacerait en entier (bandeau API compris).
    if (!allergyComplete) setAllergyTouched(true);
    if (!customAnswersComplete) {
      setCustomAnswersTouched(true);
      const firstMissing = customQuestionsRef.current?.querySelector(`[data-question-id="${firstUnansweredId}"]`);
      firstMissing?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
    return false;
  }, [stepComplete, allergyComplete, customAnswersComplete, firstUnansweredId]);

  // Enregistrer la fonction de validation auprès du parent
  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, validateStep]);

  // Verrou du bouton, posé par les seules allergies. Dépend du booléen dérivé et
  // non de `formData`, dont l'identité change à chaque frappe : l'effet ne rejoue
  // qu'au basculement, et l'étape redevient prête dès le premier caractère saisi.
  useEffect(() => {
    if (setStepReady) {
      setStepReady(allergyComplete);
    }
  }, [setStepReady, allergyComplete]);

  // Ouvrir le menu déroulant personnalisé
  const handleOpenMenu = (event) => {
    // Empêcher la propagation pour éviter la fermeture
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    // Stocker la référence pour calculer la largeur
    inputRef.current = event.currentTarget;
  };

  // Fermer le menu déroulant personnalisé
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Sélectionner une option dans le menu déroulant
  const handleSelectItem = (value) => (event) => {
    // Empêcher la propagation pour éviter la fermeture
    event.stopPropagation();
    updateFormData('occasion', value);
    handleCloseMenu();
  };

  // Handle input change pour le champ de texte
  const handleChange = (field) => (event) => {
    // Empêcher la propagation pour éviter la fermeture du modal
    event.stopPropagation();
    updateFormData(field, event.target.value);
  };

  const handleCustomAnswerChange = (questionId, value) => {
    updateFormData('customAnswers', {...formData.customAnswers, [questionId]: value});
  };

  // Gestionnaire spécifique pour empêcher la propagation sur tous les événements importants
  const handleInputClick = (event) => {
    event.stopPropagation();
  };

  // Obtenir le texte à afficher dans le sélecteur (traduit)
  const getOccasionDisplayText = () => {
    return getOccasionLabel(formData.occasion);
  };

  return (
    <Fade in timeout={400}>
      {/* Seul point d'arrêt des clics de l'étape : ils ne doivent pas remonter
          jusqu'à la fenêtre du widget, qui se fermerait. Il couvre aussi le menu
          des occasions, dont les événements remontent par l'arbre React. */}
      <Box onClick={(e) => e.stopPropagation()}>
        <DetailsContainer>
          <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
            {t ? t('widgets.reservation.form.additionalDetails') : 'Détails supplémentaires'}
          </Typography>

          {showAllergies && (
            <AllergySection
              ref={allergySectionRef}
              formData={formData}
              updateFormData={updateFormData}
              handleInputClick={handleInputClick}
              t={t}
              error={allergyTouched && !allergyComplete}
              // Aide courte sous le champ, la phrase complète restant au bandeau
              // de bas d'étape : afficher les deux ferait lire deux fois la même
              // consigne. Seule la couleur change quand le champ passe en erreur.
              helperText={
                allergyComplete ?
                  null :
                  (t ? t('widgets.reservation.form.allergy.detailsRequiredHint') : 'Obligatoire pour continuer')
              }
              onDetailsBlur={() => setAllergyTouched(true)}
            />
          )}

          {showGiftVoucher && (
            <GiftVoucherSection
              formData={formData}
              updateFormData={updateFormData}
              handleInputClick={handleInputClick}
              t={t}
            />
          )}

          {showOccasion && (
            <Box>
              <FormControl fullWidth variant="outlined" sx={{mb: 3}}>
                <InputLabel htmlFor="occasion-select">{t ? t('widgets.reservation.form.occasion') : 'Occasion spéciale'}</InputLabel>
                <CustomSelect
                  id="occasion-select"
                  label={t ? t('widgets.reservation.form.occasion') : 'Occasion spéciale'}
                  value={getOccasionDisplayText()}
                  onClick={handleOpenMenu}
                  ref={inputRef}
                  inputRef={occasionSelectRef}
                  readOnly
                  role="combobox"
                  aria-expanded={open}
                  aria-haspopup="listbox"
                  aria-controls="occasion-listbox"
                  endAdornment={
                    <InputAdornment position="end">
                      <ArrowDropDownIcon aria-hidden="true" />
                    </InputAdornment>
                  }
                  startAdornment={
                    <InputAdornment position="start">
                      <EmojiEventsIcon fontSize="small" color="primary" aria-hidden="true" />
                    </InputAdornment>
                  }
                />

                <Popper
                  open={open}
                  anchorEl={anchorEl}
                  placement="bottom-start"
                  transition
                  disablePortal={false}
                  modifiers={[
                    {
                      name: 'flip',
                      enabled: true,
                      options: {
                        fallbackPlacements: ['top-start', 'bottom-start', 'top', 'bottom'],
                      },
                    },
                    {
                      name: 'preventOverflow',
                      enabled: true,
                      options: {
                        boundary: 'viewport',
                        padding: 8,
                      },
                    },
                    {
                      name: 'offset',
                      enabled: true,
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ]}
                  style={{zIndex: 10000}}
                >
                  {({TransitionProps}) => (
                    <ClickAwayListener
                      onClickAway={handleCloseMenu}
                      mouseEvent="onMouseDown"
                      touchEvent="onTouchStart"
                    >
                      <Box>
                        <Grow {...TransitionProps}>
                          <CustomMenuPaper
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: inputRef.current ? inputRef.current.clientWidth : undefined,
                            }}
                          >
                            <List id="occasion-listbox" role="listbox" aria-label="Occasions spéciales" sx={{width: '100%', padding: 0}}>
                              {OCCASION_VALUES.map((key, index) => (
                                <React.Fragment key={key || 'none'}>
                                  <CustomListItem
                                    role="option"
                                    aria-selected={formData.occasion === key}
                                    isSelected={formData.occasion === key}
                                    onClick={handleSelectItem(key)}
                                  >
                                    <ListItemText
                                      primary={getOccasionLabel(key)}
                                    />
                                  </CustomListItem>
                                  {index < OCCASION_VALUES.length - 1 && (
                                    <Divider component="li" />
                                  )}
                                </React.Fragment>
                              ))}
                            </List>
                          </CustomMenuPaper>
                        </Grow>
                      </Box>
                    </ClickAwayListener>
                  )}
                </Popper>
              </FormControl>
            </Box>
          )}

          {showSpecialRequests && (
            <Box>
              <Paper
                elevation={0}
                sx={{mb: 3, position: 'relative'}}
              >
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="special-requests">{t ? t('widgets.reservation.form.specialRequests') : 'Demandes spéciales'}</InputLabel>
                  <SpecialRequestsInput
                    id="special-requests"
                    label={t ? t('widgets.reservation.form.specialRequests') : 'Demandes spéciales'}
                    multiline
                    rows={4}
                    value={formData.specialRequests || ''}
                    onChange={handleChange('specialRequests')}
                    onClick={handleInputClick}
                    onFocus={handleInputClick}
                    onMouseDown={handleInputClick}
                    onMouseUp={handleInputClick}
                    placeholder={t ? t('widgets.reservation.form.specialRequestsPlaceholder') : 'Précisez vos demandes spéciales...'}
                    startAdornment={
                      <InputAdornment position="start" sx={{alignSelf: 'flex-start', mt: 1.5}}>
                        <CommentIcon fontSize="small" color="primary" aria-hidden="true" />
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Paper>
            </Box>
          )}

          <Box ref={customQuestionsRef}>
            <CustomQuestionsSection
              questions={customQuestions}
              values={formData.customAnswers}
              onAnswerChange={handleCustomAnswerChange}
              showErrors={customAnswersTouched}
              handleInputClick={handleInputClick}
              t={t}
            />
          </Box>

          {/* Dernier élément du contenu défilable, donc juste au-dessus du bouton
              resté gris : c'est là que le regard se porte quand le client a rempli
              les autres champs et redescendu la page sans comprendre le blocage.
              Rien n'est ajouté dans le pied de page, dont la hauteur est réservée
              en dur par le conteneur défilant. */}
          {!allergyComplete && (
            // Même rythme vertical que les autres blocs de l'étape, qui portent
            // tous leur marge basse : l'espace au-dessus vient du bloc précédent.
            <InlineNotice data-testid="allergy-details-required" sx={{mb: 3}}>
              {allergyMessage}
            </InlineNotice>
          )}

          {customAnswersTouched && !customAnswersComplete && (
            <InlineNotice data-testid="custom-answers-required" sx={{mb: 3}}>
              {t ?
                t('widgets.reservation.form.customQuestions.requiredNotice') :
                'Merci de répondre aux questions marquées d\'un astérisque (*) pour continuer.'}
            </InlineNotice>
          )}

          {errors.api && (
            <Alert severity="error" sx={{mt: 2, p: 2}} variant="filled">
              {errors.api}
            </Alert>
          )}
        </DetailsContainer>
      </Box>
    </Fade>
  );
}

AdditionalDetailsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
  t: PropTypes.func, // Translation function
};
